import { readSearchParam } from 'src/common/readSearchParam.ts';
import { LOCATION_STORE_PARAM_NAME } from 'src/common/constants.ts';
import {
    initLocationState as initActuatorLocationState,
    isLocationState as isActuatorLocationState,
} from 'src/actuators/locationStateContext.ts';
import { useContext, useMemo } from 'react';
import { ConfigContext } from 'src/configContext.ts';
import { usePriceQuery } from 'src/projects/hooks/usePriceQuery.ts';
import {
    FetchActuatorSelectionDocument,
    FetchActuatorSelectionQuery,
    FetchActuatorSelectionQueryVariables,
} from 'src/generated/operations.ts';
import { SelectionDataValue } from 'src/projects/components/SelectionDataContext.ts';
import { safeTotal } from 'src/projects/utils/SafeTotal.ts';
import { calculateTotals as calculateActuatorTotals } from 'src/actuators/checkout/CalculateTotals.ts';
import { Provide, ProviderProps } from 'src/projects/components/SelectionDataProvider.tsx';
import { removeDuplicateFragments } from 'src/utils/de-dupe-fragments.ts';

export const FetchActuatorSelectionDedupedDocument = removeDuplicateFragments(FetchActuatorSelectionDocument);

export const ActuatorProvider = ({ selection, children }: ProviderProps) => {
    const locationState = readSearchParam(
        new URLSearchParams(selection.searchQuery),
        LOCATION_STORE_PARAM_NAME,
        { ...initActuatorLocationState },
        isActuatorLocationState,
    );
    const { userSettings, currency } = useContext(ConfigContext);

    const { data } = usePriceQuery<FetchActuatorSelectionQuery, FetchActuatorSelectionQueryVariables>(
        FetchActuatorSelectionDedupedDocument,
        {
            variables: {
                region: userSettings.currentRegion,
                locale: userSettings.currentLocale,
                label: locationState.actuatorLabel,
            },
        },
    );

    const value = useMemo<SelectionDataValue>(() => {
        if (data === undefined) {
            return { loading: true, total: null, currency: currency ?? 'USD', data: undefined };
        }
        const { total, currency: cur } = safeTotal(
            () => calculateActuatorTotals(data.accessories, userSettings, locationState, currency),
            currency ?? 'USD',
        );
        return { loading: false, total, currency: cur, data };
    }, [data, currency, userSettings, locationState]);

    return <Provide value={value}>{children}</Provide>;
};
